import React from 'react';
import { Box, Container, Typography, Divider, Link, useTheme } from '@mui/material';
import useSeoHead from '../marketing/useSeoHead';

const PrivacyPolicyPage: React.FC = () => {
  const { palette } = useTheme();
  useSeoHead({
    title: 'Privacy Policy | MillOS',
    description: 'How MillOS collects, uses and protects your personal data — account information, Google/Gmail access, uploaded documents, bank data and more.',
    canonicalPath: '/privacy',
  });
  return (
    <Box sx={{ backgroundColor: palette.tokens.canvas, minHeight: '100vh', py: 6 }}>
      <Container maxWidth="md">
        <Box sx={{ backgroundColor: palette.tokens.white, borderRadius: 2, p: 5, boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
          <Typography variant="h4" fontWeight={700} gutterBottom>
            Privacy Policy
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Last updated: 31 October 2026
          </Typography>
          <Divider sx={{ my: 3 }} />

          <Typography variant="body1" paragraph>
            MillOS ("<strong>we</strong>", "<strong>our</strong>", "<strong>us</strong>") operates the website{' '}
            <Link href="https://www.millos.uk">www.millos.uk</Link> (the "<strong>Service</strong>").
            This page explains how we collect, use, and protect your personal data when you use our Service.
          </Typography>

          <Typography variant="h6" fontWeight={600} gutterBottom mt={3}>
            1. What Data We Collect
          </Typography>
          <Typography variant="body1" paragraph>
            When you create an account and use the Service, we may collect the following:
          </Typography>
          <Box component="ul" sx={{ pl: 3 }}>
            <li><Typography variant="body1"><strong>Account information</strong>: your name and email address used to register.</Typography></li>
            <li><Typography variant="body1"><strong>Google account data</strong>: if you connect your Google account, we access your Gmail inbox and Google Calendar. For Gmail, we only read emails from senders that you have explicitly added to your list of trusted certificate providers — we never scan or access any other emails in your inbox. For Calendar, we only create and manage certificate expiry reminders.</Typography></li>
            <li><Typography variant="body1"><strong>Uploaded documents</strong>: PDF certificates and invoices you upload to the platform.</Typography></li>
            <li><Typography variant="body1"><strong>Bank account data</strong>: if you choose to connect a bank account via TrueLayer, we collect and store transaction data (amounts, dates, merchant names, categories) from that account. This requires your explicit consent and two-factor authentication before connecting, and is currently limited to TrueLayer's Sandbox test environment.</Typography></li>
            <li><Typography variant="body1"><strong>Property address searches</strong>: when you use the address finder to locate a property, the text you type is sent to LocationIQ (an address-lookup service) to find matching real addresses. If you pick a match, we store its coordinates so the property can be shown on a map — you can also just type your own address instead, which is never sent to LocationIQ.</Typography></li>
            <li><Typography variant="body1"><strong>Usage data</strong>: basic server logs (IP address, browser type, pages visited) for security and debugging purposes.</Typography></li>
          </Box>

          <Typography variant="h6" fontWeight={600} gutterBottom mt={3}>
            2. How We Use Your Data
          </Typography>
          <Box component="ul" sx={{ pl: 3 }}>
            <li><Typography variant="body1">To provide the certificate management features of the Service.</Typography></li>
            <li><Typography variant="body1">To monitor your connected Gmail inbox for incoming property certificate emails and automatically extract certificate information on your behalf.</Typography></li>
            <li><Typography variant="body1">To create Google Calendar events reminding you of upcoming certificate expiry dates.</Typography></li>
            <li><Typography variant="body1">To send reminder emails to you or your tenants/contractors about certificate renewals, using your connected Gmail account.</Typography></li>
            <li><Typography variant="body1">To improve and maintain the Service.</Typography></li>
          </Box>

          <Typography variant="h6" fontWeight={600} gutterBottom mt={3}>
            3. Google API Scopes
          </Typography>
          <Typography variant="body1" paragraph>
            If you connect your Google account, we request the following permissions:
          </Typography>
          <Box component="ul" sx={{ pl: 3 }}>
            <li><Typography variant="body1"><strong>gmail.modify</strong>: to read incoming emails from your trusted certificate providers and mark them as processed. We only access emails from senders you have explicitly added — never your full inbox.</Typography></li>
            <li><Typography variant="body1"><strong>gmail.compose</strong>: to send reminder emails on your behalf.</Typography></li>
            <li><Typography variant="body1"><strong>calendar.events</strong>: to create and manage certificate expiry reminders in your calendar.</Typography></li>
            <li><Typography variant="body1"><strong>userinfo.email</strong>: to identify which Google account is connected.</Typography></li>
          </Box>
          <Typography variant="body1" paragraph>
            Our use of Google user data complies with the{' '}
            <Link href="https://developers.google.com/terms/api-services-user-data-policy" target="_blank" rel="noopener noreferrer">
              Google API Services User Data Policy
            </Link>
            , including the Limited Use requirements.
          </Typography>

          <Typography variant="h6" fontWeight={600} gutterBottom mt={3}>
            4. Data Sharing
          </Typography>
          <Typography variant="body1" paragraph>
            We do not sell, rent, or share your personal data with third parties, except:
          </Typography>
          <Box component="ul" sx={{ pl: 3 }}>
            <li><Typography variant="body1"><strong>Google APIs</strong>: to provide Gmail and Calendar integration (governed by Google's own privacy policy).</Typography></li>
            <li><Typography variant="body1"><strong>Google Cloud</strong>: we use Google Cloud infrastructure (Cloud Run, Cloud SQL, Cloud Storage) to host and store data securely within the EU (europe-west1 region).</Typography></li>
            <li><Typography variant="body1"><strong>Google Gemini AI</strong>: uploaded certificate PDFs are processed by Google Gemini AI to extract data. No personally identifiable information is used to train models.</Typography></li>
            <li><Typography variant="body1"><strong>TrueLayer</strong>: if you connect a bank account, TrueLayer facilitates that connection and the retrieval of transaction data on our behalf, governed by TrueLayer's own privacy policy.</Typography></li>
            <li><Typography variant="body1"><strong>LocationIQ</strong>: if you use the property address finder, the text you type is sent to LocationIQ to find matching addresses, governed by LocationIQ's own privacy policy.</Typography></li>
          </Box>

          <Typography variant="h6" fontWeight={600} gutterBottom mt={3}>
            5. Data Retention
          </Typography>
          <Typography variant="body1" paragraph>
            Your data is retained for as long as your account is active. You may request deletion of your account and associated data at any time by contacting us at the email below. Bank transaction data specifically is retained for a maximum of 24 months and is automatically deleted after that; disconnecting a bank account deletes its transaction data immediately. This retention policy is reviewed at least annually, and whenever we add a new data source or new category of sensitive data.
          </Typography>

          <Typography variant="h6" fontWeight={600} gutterBottom mt={3}>
            6. Security
          </Typography>
          <Typography variant="body1" paragraph>
            All data is stored on Google Cloud infrastructure within the EU. Passwords are hashed and never stored in plain text. All connections use HTTPS. Google OAuth tokens are stored securely and never exposed to the frontend. Bank account access tokens and transaction details are encrypted at rest, and connecting a bank account requires two-factor authentication in addition to your regular login.
          </Typography>

          <Typography variant="h6" fontWeight={600} gutterBottom mt={3}>
            7. Your Rights
          </Typography>
          <Typography variant="body1" paragraph>
            Under UK GDPR, you have the right to access, correct, or delete your personal data. You can disconnect your Google account at any time from the Integrations page, which revokes our access to your Gmail and Calendar.
          </Typography>

          <Typography variant="h6" fontWeight={600} gutterBottom mt={3}>
            8. Contact
          </Typography>
          <Typography variant="body1" paragraph>
            For any privacy-related questions or data requests, please contact:{' '}
            <Link href="mailto:victor.melatti01@gmail.com">victor.melatti01@gmail.com</Link>
          </Typography>

          <Divider sx={{ my: 3 }} />
          <Typography variant="body2" color="text.secondary">
            © {new Date().getFullYear()} MillOS. All rights reserved.
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default PrivacyPolicyPage;
